import React from 'react';
import { useCategory } from '@/contexts/CategoryManagementContext';
import { calculateDaysRemaining } from '@/lib/utils';
import { motion } from 'framer-motion';
import { AlertCircle, Clock, CheckCircle, BellRing } from 'lucide-react';

const normalizeLabel = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const DailyAnalysisWidget = ({ goals, services, attendance, implementations }) => {
  const { categories, getCategoryByDays } = useCategory();

  // Aggregate all items
  const allItems = [
    ...(goals || []),
    ...(services || []),
    ...(attendance || []),
    ...(implementations || [])
  ].filter(i => i.lifecycleStatus === 'Aberto');

  // Count per category
  const counts = categories.reduce((acc, cat) => {
    acc[cat.id] = 0;
    return acc;
  }, {});

  allItems.forEach(item => {
    if (item.dueDate) {
      const days = calculateDaysRemaining(item.dueDate);
      const category = getCategoryByDays(days);
      if (category && counts[category.id] !== undefined) {
        counts[category.id]++;
      }
    }
  });

  // Helper to map icon to category name (simple heuristic)
  const getIcon = (name) => {
    const n = normalizeLabel(name);
    if (n.includes('vencida')) return <AlertCircle className="w-6 h-6 text-white" />;
    if (n.includes('atencao')) return <BellRing className="w-6 h-6 text-white" />;
    if (n.includes('proximo')) return <Clock className="w-6 h-6 text-white" />;
    return <CheckCircle className="w-6 h-6 text-white" />;
  };

  const getDisplayCategoryName = (name) => {
    const normalized = normalizeLabel(name);
    if (normalized.includes('vencida') || normalized.includes('venida')) {
      return 'Vencida';
    }
    return name;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {categories.map((cat, index) => (
        <motion.div
          key={cat.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className="relative overflow-hidden rounded-xl p-6 shadow-lg border border-white/10"
          style={{
            background: `linear-gradient(135deg, ${cat.color}20 0%, ${cat.color}05 100%)`,
            borderColor: `${cat.color}30`
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div 
              className="p-3 rounded-lg shadow-inner"
              style={{ backgroundColor: cat.color }}
            >
              {getIcon(cat.name)}
            </div>
            <span 
              className="text-3xl font-black"
              style={{ color: cat.color }}
            >
              {counts[cat.id] || 0}
            </span>
          </div>
          
          <div>
            <h3 className="text-white font-bold text-lg mb-1">{getDisplayCategoryName(cat.name)}</h3>
            <p className="text-gray-400 text-xs font-medium">{cat.description}</p>
          </div>

          {/* Decorative blur */}
          <div 
            className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: cat.color }}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default DailyAnalysisWidget;